// src/pages/stock/EsimBulkUpdateDetail.tsx
//
// EsimBulkUpdateDetail
// - Admin page to review a bulk-update run before confirming.
// - Summary: stock + filename + counts + run status.
// - Table: per-row preview of update results, with "Only Failure" filter.
// - Confirm: available only when the run is PENDING and not deleted.
// - Table columns focus on the change itself (state/expiry) and the result.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { confirmUpdate } from '@apis/esimBulk/update';
import type {
  GetUpdateDetailsResponse,
  GetUpdateHistoriesResponse,
} from '@apis/esimBulk/update';

import { useUpdateDetail } from '@queries/esimBulk/useUpdateDetail';
import { useUpdateDetails } from '@queries/esimBulk/useUpdateDetails';
import { useUpdateDetailsCount } from '@queries/esimBulk/useUpdateDetailsCount';

import useAsyncEffect from '@hooks/useAsyncEffect';

import { timestampToDate } from '@utils/time';
import { Status, UpdateStatus } from '@models/esim';

import { usePageHeader } from '@components/common/pageHeader.context';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

const formatNumber = (n: number | undefined | null) =>
  Number(n ?? 0).toLocaleString();

const dash = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const HeaderLabel = ({ label, hint }: { label: string; hint?: string }) => {
  if (!hint) return <>{label}</>;
  return (
    <Tooltip title={hint} arrow>
      <Box component="span" sx={{ cursor: 'help' }}>
        {label}
      </Box>
    </Tooltip>
  );
};

/**
 * Render a chip for row-level update result.
 * (UI-only mapping; backend value is still displayed when unknown)
 */
const UpdateStatusChip = ({ status }: { status: UpdateStatus }) => {
  switch (status) {
    case UpdateStatus.OK:
      return <Chip size="small" label="OK" color="primary" variant="outlined" />;
    case UpdateStatus.DUPLICATE:
      return (
        <Chip size="small" label="DUPLICATE" color="warning" variant="filled" />
      );
    case UpdateStatus.ALREADY_EXIST:
      return (
        <Chip size="small" label="ALREADY EXIST" color="error" variant="filled" />
      );
    default:
      return <Chip size="small" label={dash(status)} variant="outlined" />;
  }
};

/**
 * Render a chip for run-level status (PENDING/PROCESSING/COMPLETED),
 * with a Deleted override.
 */
const RunStatusChip = ({
  status,
  isDeleted,
}: {
  status: Status;
  isDeleted: boolean;
}) => {
  if (isDeleted) return <Chip size="small" label="Deleted" variant="filled" />;

  switch (status) {
    case Status.COMPLETED:
      return <Chip size="small" label="Completed" color="success" variant="filled" />;
    case Status.PROCESSING:
      return (
        <Chip size="small" label="Processing" color="primary" variant="outlined" />
      );
    case Status.PENDING:
      return <Chip size="small" label="Waiting" color="warning" variant="filled" />;
    default:
      return <Chip size="small" label={dash(status)} variant="outlined" />;
  }
};

export const EsimBulkUpdateDetail = () => {
  const { updateId } = useParams();
  const navigate = useNavigate();

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const updateIdNumber = useMemo(() => Number(updateId), [updateId]);

  const [onlyFailed, setOnlyFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [isConfirming, setIsConfirming] = useState(false);

  const tableTopRef = useRef<HTMLDivElement | null>(null);

  const [summary, setSummary] = useState<GetUpdateHistoriesResponse>({
    esimInventoryBulkUpdateId: 0,
    createdAt: 0,
    filename: '',
    isDeleted: false,
    stock: { stockCode: '', stockName: '' },
    updatedAt: 0,
    okayCount: 0,
    failedCount: 0,
    totalCount: 0,
    status: Status.PENDING,
  });

  const { data: updateDetail, isLoading: isSummaryLoading } =
    useUpdateDetail(updateIdNumber);

  const { data: updateDetailsData, isLoading: isRowsLoading } = useUpdateDetails({
    updateId: updateIdNumber,
    page,
    pageSize: PAGE_SIZE,
    params: { onlyFailed },
  });

  const { data: totalCount, isLoading: isCountLoading } =
    useUpdateDetailsCount(updateIdNumber);

  useAsyncEffect(async () => {
    if (!updateDetail) return;
    setSummary(updateDetail.data?.[0]);
  }, [updateDetail]);

  /**
   * Backend filtering (`onlyFailed=true`) may not be fully reliable.
   * We keep UI-level filtering to ensure the "Only Failure" toggle always behaves.
   *
   * Lint fix (react-hooks/exhaustive-deps):
   * - Avoid creating `rawRows` outside and referencing it in deps.
   * - Derive rows directly from `updateDetailsData?.data` inside useMemo.
   */
  const rows = useMemo<GetUpdateDetailsResponse[]>(() => {
    const list = updateDetailsData?.data ?? [];
    if (!onlyFailed) return list;
    return list.filter((r) => r.updateStatus !== UpdateStatus.OK);
  }, [onlyFailed, updateDetailsData?.data]);

  const isTableLoading = Boolean(isSummaryLoading || isRowsLoading || isCountLoading);

  const handleToggleOnlyFailure = useCallback(() => {
    setOnlyFailed((prev) => !prev);
    setPage(1);

    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const copyActivation = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        systemAlert.success('Activation copied.');
      } catch {
        systemAlert.error('Copy failed.');
      }
    },
    [systemAlert],
  );

  const handleConfirm = useCallback(async () => {
    if (!Number.isFinite(updateIdNumber) || updateIdNumber <= 0) return;
    if (isConfirming) return;

    const ok = await confirm({
      priority: 'warning',
      title: 'Confirm this update?',
      description:
        'Once confirmed, the inventory updates will be finalized and may not be editable.',
      confirmText: 'Confirm Update',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });
    if (!ok) return;

    setIsConfirming(true);
    try {
      const { message } = await confirmUpdate(updateIdNumber);

      if (message !== 'OK') {
        systemAlert.error(message || 'Update confirmation failed.');
        return;
      }

      systemAlert.success('Update confirmed successfully.');
      navigate('/stocks/update');
    } catch {
      systemAlert.error('Update confirmation failed.');
    } finally {
      setIsConfirming(false);
    }
  }, [confirm, isConfirming, navigate, systemAlert, updateIdNumber]);

  /**
   * AppPageHeader slots
   * - leading.title: stock code + stock name (name is less emphasized)
   * - leading.description: filename
   * - actions: show confirm button only when the run is PENDING and not deleted
   */
  useEffect(() => {
    const stockCode = summary.stock?.stockCode?.trim();
    const stockName = summary.stock?.stockName?.trim();
    const filename = summary.filename?.trim();

    const canConfirm =
      !summary.isDeleted &&
      summary.status === Status.PENDING &&
      Number.isFinite(updateIdNumber) &&
      updateIdNumber > 0;

    const leadingTitle =
      stockCode || stockName ? (
        <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
          {stockCode && (
            <Typography component="span" variant="h6" fontWeight={900}>
              {stockCode}
            </Typography>
          )}
          {stockCode && stockName && (
            <Typography component="span" variant="h6" color="text.secondary">
              :
            </Typography>
          )}
          {stockName && (
            <Typography
              component="span"
              variant="subtitle1"
              color="text.secondary"
              fontWeight={700}
            >
              {stockName}
            </Typography>
          )}
        </Box>
      ) : undefined;

    setPageHeaderSlots({
      leading: {
        title: leadingTitle,
        description: filename ? `File: ${filename}` : undefined,
      },
      actions: canConfirm
        ? [
            {
              key: 'confirm-update',
              label: isConfirming ? 'Confirming…' : 'Confirm Update',
              onClick: handleConfirm,
              variant: 'contained',
              primary: true,
              disabled: isConfirming,
            },
          ]
        : undefined,
    });
  }, [
    handleConfirm,
    isConfirming,
    setPageHeaderSlots,
    summary.filename,
    summary.isDeleted,
    summary.status,
    summary.stock?.stockCode,
    summary.stock?.stockName,
    updateIdNumber,
  ]);

  return (
    <>
      {/* Summary */}
      <Box mt={2}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>
              Update Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stock, file and result overview for this update run.
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Stock Code
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={800} sx={cellSx.nowrap}>
                  {dash(summary.stock?.stockCode)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Stock Name
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={700} sx={cellSx.nowrap}>
                  {dash(summary.stock?.stockName)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                File
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    ...cellSx.nowrap,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    maxWidth: '100%',
                  }}
                  title={summary.filename}
                >
                  {dash(summary.filename)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={800} sx={cellSx.number}>
                  {formatNumber(summary.totalCount)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                OK
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ ...cellSx.number, color: 'success.main' }}
                >
                  {formatNumber(summary.okayCount)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Fail
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography
                  variant="body2"
                  fontWeight={(summary.failedCount ?? 0) > 0 ? 800 : 600}
                  sx={{
                    ...cellSx.number,
                    color: (summary.failedCount ?? 0) > 0 ? 'error.main' : 'text.secondary',
                  }}
                >
                  {formatNumber(summary.failedCount)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Box mt={0.5}>
                  <RunStatusChip
                    status={summary.status}
                    isDeleted={Boolean(summary.isDeleted)}
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Created At
              </Typography>
              {isSummaryLoading ? (
                <Skeleton height={28} />
              ) : (
                <Typography variant="body2" fontWeight={700} sx={cellSx.nowrap}>
                  {summary.createdAt ? timestampToDate(summary.createdAt) : '-'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Filter + Table */}
      <Box mt={2} ref={tableTopRef}>
        <Box
          mb={1}
          display="flex"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Typography variant="subtitle1" fontWeight={800}>
            Update Details
          </Typography>

          <FormControlLabel
            control={
              <Switch checked={onlyFailed} onChange={handleToggleOnlyFailure} color="warning" />
            }
            label="Only Failure"
          />
        </Box>

        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount ?? 0}
          onChangePage={setPage}
        />

        <StandardTableFrame<GetUpdateDetailsResponse>
          head={
            <TableRow>
              <TableCell align="right" sx={cellSx.nowrap}>
                <HeaderLabel label="#" hint="Index (#)" />
              </TableCell>

              <TableCell align="center" sx={cellSx.nowrap}>
                <HeaderLabel label="Redeem" hint="Redemption Code" />
              </TableCell>

              <TableCell align="left" sx={cellSx.nowrap}>
                <HeaderLabel label="Activation" hint="LPA URL (Activation Code)" />
              </TableCell>

              <TableCell align="center" sx={cellSx.nowrap}>
                <HeaderLabel label="State" hint="State change (From → To)" />
              </TableCell>

              <TableCell align="center" sx={cellSx.nowrap}>
                <HeaderLabel label="Expiry" hint="Expiry change (From → To)" />
              </TableCell>

              <TableCell align="center" sx={cellSx.nowrap}>
                <HeaderLabel label="Result" hint="Update result" />
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => row.esimInventoryBulkUpdateDetailId}
          columnsCount={6}
          tableSx={{ minWidth: 1100 }}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          emptyText={onlyFailed ? 'No failed rows found.' : 'No update details found.'}
          getRowStatus={(row) => (row.updateStatus !== UpdateStatus.OK ? 'error' : 'normal')}
          renderRow={(row, index) => {
            const activation = dash(row.lpaUrl);
            const canCopy = activation !== '-';

            const stateChanged =
              row.esimStatusFrom !== undefined &&
              row.esimStatusTo !== undefined &&
              row.esimStatusFrom !== row.esimStatusTo;

            const stateChangeText = stateChanged
              ? `${dash(row.esimStatusFrom)} → ${dash(row.esimStatusTo)}`
              : '-';

            const expiryChanged =
              row.expireAtFrom !== undefined &&
              row.expireAtTo !== undefined &&
              row.expireAtFrom !== row.expireAtTo;

            const expiryChangeText = expiryChanged
              ? `${timestampToDate(row.expireAtFrom!, 'YYYY-MM-DD')} → ${timestampToDate(
                  row.expireAtTo!,
                  'YYYY-MM-DD',
                )}`
              : '-';

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell align="center" sx={cellSx.mono}>
                  {dash(row.redemptionCode)}
                </TableCell>

                <TableCell
                  align="left"
                  sx={{
                    ...cellSx.nowrap,
                    maxWidth: 520,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                    {activation === '-' ? (
                      <Box component="span">-</Box>
                    ) : (
                      <Tooltip title={activation} arrow>
                        <Box
                          component="span"
                          sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {activation}
                        </Box>
                      </Tooltip>
                    )}

                    {canCopy && (
                      <Tooltip title="Copy" arrow>
                        <IconButton
                          size="small"
                          onClick={() => copyActivation(String(activation))}
                          aria-label="copy activation"
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {stateChangeText}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {expiryChangeText}
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <UpdateStatusChip status={row.updateStatus} />
                </TableCell>
              </>
            );
          }}
        />

        <Box mt={2}>
          <StandardPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount ?? 0}
            onChangePage={setPage}
          />
        </Box>
      </Box>
    </>
  );
};
